import * as Sentry from "@sentry/browser";
import ysFixWebmDuration from "fix-webm-duration";
import { useEffect, useRef, useState } from "react";

import { isFirefox, isIOS, isSafari } from "../../utils/device";
import { CATEGORIES, useSendGAEvent } from "../../utils/googleAnalytics";

type UseVideoRecordingParams = {
  gaEventCategory: CATEGORIES;
  onRecordingAvailable(recording: Blob): void;
  onError(error: Error): void;
  includeVideo?: boolean;
  includeAudio?: boolean;
  videoConstraints?: MediaTrackConstraints;
  audioConstraints?: MediaTrackConstraints | boolean;
};

export type VideoRecording = {
  isRecording: boolean;
  startRecording(): Promise<MediaRecorder>;
  stopRecording(): void;
  mimeType: string;
  stream: MediaStream | null;
};

const getVideoMimeType = (
  MediaRecorderCtor: typeof MediaRecorder | undefined
): string => {
  if (!MediaRecorderCtor) {
    return "video/webm";
  }

  const webKitPreferredTypes = [
    "video/mp4",
    "video/webm;codecs=vp9",
    "video/webm;codecs=vp8",
    "video/webm",
  ];
  const firefoxPreferredTypes = ["video/webm"];
  const defaultPreferredTypes = [
    "video/webm;codecs=vp9",
    "video/webm;codecs=vp8",
    "video/webm",
    "video/mp4",
  ];

  // iOS browsers (including Firefox/Chrome on iOS) all run on WebKit.
  const isWebKitRecorder = isSafari() || isIOS();
  const preferredTypes = isWebKitRecorder
    ? webKitPreferredTypes
    : isFirefox()
    ? firefoxPreferredTypes
    : defaultPreferredTypes;

  const supportedType = preferredTypes.find((type) =>
    MediaRecorderCtor.isTypeSupported(type)
  );
  return supportedType ?? "video/webm"; // fallback
};

export const useVideoRecording = ({
  gaEventCategory,
  onRecordingAvailable,
  onError,
  includeVideo = true,
  includeAudio = true,
  videoConstraints,
  audioConstraints,
}: UseVideoRecordingParams): VideoRecording => {
  const [isRecording, setIsRecording] = useState(false);
  const [stream, setStream] = useState<MediaStream | null>(null);
  const sendGAEvent = useSendGAEvent();

  const onRecordingAvailableRef = useRef(onRecordingAvailable);
  const onErrorRef = useRef(onError);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);

  useEffect(() => {
    onRecordingAvailableRef.current = onRecordingAvailable;
    onErrorRef.current = onError;
  }, [onRecordingAvailable, onError]);

  useEffect(() => {
    return () => {
      // Cleanup on unmount
      if (recorderRef.current && recorderRef.current.state !== "inactive") {
        recorderRef.current.stop();
      }
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
      }
    };
  }, []);

  const MediaRecorderCtor = globalThis.MediaRecorder;
  const mimeType = getVideoMimeType(MediaRecorderCtor);

  return {
    isRecording,
    mimeType,
    stream,

    async startRecording() {
      try {
        if (!MediaRecorderCtor) {
          throw new Error("MediaRecorder is not supported in this browser");
        }

        const defaultVideoConstraints: MediaTrackConstraints = {
          facingMode: "user",
          width: { ideal: 1280 },
          height: { ideal: 720 },
        };

        const constraints: MediaStreamConstraints = {
          video: includeVideo
            ? videoConstraints ?? defaultVideoConstraints
            : false,
          audio: includeAudio ? audioConstraints ?? true : false,
        };

        const mediaStream = await navigator.mediaDevices.getUserMedia(
          constraints
        );
        streamRef.current = mediaStream;
        setStream(mediaStream);

        const startTime = Date.now();
        const recorder = new MediaRecorderCtor(mediaStream, {
          mimeType,
          videoBitsPerSecond: 2500000, // 2.5 Mbps for good quality
        });
        recorderRef.current = recorder;

        const chunks: BlobPart[] = [];
        recorder.addEventListener("dataavailable", (e) => {
          if (e.data?.size) chunks.push(e.data);
        });

        recorder.addEventListener("error", (e) => {
          sendGAEvent(
            "error",
            gaEventCategory,
            "Recording error",
            e.error?.message || "Unknown error"
          );
          Sentry.captureException(e.error || new Error("MediaRecorder error"));
          onErrorRef.current?.(e.error || new Error("Recording failed"));
          setIsRecording(false);
        });

        recorder.addEventListener("stop", async () => {
          const videoBlob = new Blob(chunks, {
            type: mimeType,
          });

          const duration = Date.now() - startTime;

          if (mimeType.includes("webm")) {
            // Fix webm duration metadata
            const blobWithDurationMetadata = await ysFixWebmDuration(
              videoBlob,
              duration,
              { logger: false }
            );
            onRecordingAvailableRef.current?.(blobWithDurationMetadata);
          } else {
            onRecordingAvailableRef.current?.(videoBlob);
          }

          setIsRecording(false);
          // Stop all tracks
          mediaStream.getTracks().forEach((track) => track.stop());
          setStream(null);
          streamRef.current = null;
        });

        // Start recording, collecting data every 1 second
        recorder.start(1000);
        setIsRecording(true);

        return recorder;
      } catch (err) {
        const error =
          err instanceof Error ? err : new Error("Failed to start recording");
        sendGAEvent(
          "error",
          gaEventCategory,
          "Failed to start recording",
          error.message
        );
        Sentry.captureException(error);
        onErrorRef.current?.(error);
        setIsRecording(false);
        throw error;
      }
    },

    stopRecording() {
      if (recorderRef.current && recorderRef.current.state !== "inactive") {
        recorderRef.current.stop();
      }
    },
  };
};
